import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { AnyObjectSchema } from 'yup'
import { Step1, Step2 } from '../../components/onboarding-forms'
import { onboardingStep1Schema } from '../../components/onboarding-forms/schema'
import { otpSchema } from '../../components/otp-input/otpSchema'
import { SIGNUP_FORM } from '../../containers/_nav'
import {
    handleMiddleLayerAuthentication,
    middleLayerRequestOtp,
} from '../../services/auth'
import { Axios } from '../../services/axios'
import {
    MIDDLELAYER_RESPONSE_CODE,
    ROUTES,
    SIGNUP_FORM_TYPE,
} from '../../utils/constants/enum'
import { FunctionalComponentWithChildren } from '../../utils/types/React'
import {
    ISubAccount,
    IUnregisteredUserData,
    MultiAccountResponse,
    OnboardingStepsFormType,
    SignInFormTypes,
} from '../../utils/types/auth'

import { FormikHelpers } from 'formik'
import { UserExistsModal } from '../../components/onboarding-forms/onboarding-step2/modal/user-exists'
import { MixPanel } from '../../services/mixpanelActions'
import {
    MESSAGES,
    USER_AUTHENTICATION_TYPE,
    UserRole,
} from '../../utils/constants'
import {
    // isValidOtpResponse,
    OTP_LANGUAGE,
} from '../../utils/helper-functions'
import { getLocalState } from '../../utils/local-storage'
import { AuthContext } from './Auth'
import { UserDataContext } from './UserDataProvider'

export interface PageType {
    title: string
    schema: AnyObjectSchema
    component: ReactNode
}

export interface IMultiLocationSignUp {
    acceptTerms: boolean
    businessName: string
    firstName: string
    lastName: string
    emailAddress: string
    mobilePhoneNumber: string
    optInMarketingByEmail: boolean
    optInMarketingByPhone: boolean
    country: string
    userRole: string
    beautyHealthCustomerId?: string
    idToken: string
    accessToken: string
    refreshToken: string
    appUserKey?: string
    mlpSingleSignUp: boolean
    initiateEstiSignUp?: boolean
}

export interface IOnboardingDetails {
    session: string
    acceptTerms: boolean
    appUserKey: string | undefined
    businessName?: string
    country: string
    emailAddress: string
    firstName: string
    lastName: string
    mobilePhoneNumber: string
    code?: string
    optInMarketingByEmail: boolean
    optInMarketingByPhone: boolean
    userRole: string
    beautyHealthCustomerId: string | undefined
    optInDataSharingAgreement: boolean
    idToken?: string
    accessToken?: string
    refreshToken?: string
    mobilePhoneCountry?: string
}
interface OnboardingContextType {
    page?: PageType
    progress: number
    stepIndex: number
    handleSignup: (
        values: OnboardingStepsFormType,
        helpers: FormikHelpers<OnboardingStepsFormType>
    ) => Promise<void>
    handleBack: () => void
    handleResendOtp: (
        username: string,
        countrySelected: string,
        isResendRequest?: boolean
    ) => Promise<void>
    submittedOtp: string
    isOtpSameAsLast: boolean
    setIsOtpSameAsLast: any
    otpError: string
    multiAccount: MultiAccountResponse
    setMultiAccount: (value: MultiAccountResponse) => void
    setStep: (value: SIGNUP_FORM_TYPE | undefined) => void
    allProvidersSignedUp: boolean | undefined
    onboardingDetails: IOnboardingDetails | null
    setOnboardingDetails: (value: IOnboardingDetails | null) => void
    otpSession: string
    selectedAccount: ISubAccount | null
    setSelectedAccount: (value: ISubAccount | null) => void
    unregisteredUserData: IUnregisteredUserData | null
    isLoading: boolean | null
    setLoading: (value: boolean | null) => void
    mlSignupError: boolean | string
    clearAuthenticationFlow: () => void
    setMlSignupError: (value: boolean | string) => void
    signUp: (data: IMultiLocationSignUp) => Promise<void>
    signInDetails: SignInFormTypes | null
}

export const OnboardingFormContext = createContext<OnboardingContextType>(
    {} as OnboardingContextType
)

const OnboardingFormProvider: FunctionalComponentWithChildren = ({
    children,
}) => {
    const navigate = useNavigate()
    const { step } = useParams<{ step: SIGNUP_FORM_TYPE }>()
    const { t } = useTranslation()
    const location = useLocation()

    const totalForms = SIGNUP_FORM.length
    const [stepIndex, setStepIndex] = useState<number>(0)
    const [progress, setProgress] = useState<number>(100 / totalForms)
    const [otpSession, setOtpSession] = useState<string>('')
    const [page, setPage] = useState<PageType>()
    const [userExists, setUserExists] = useState(false)
    const [submittedOtp, setSubmittedOtp] = useState('')
    const [isOtpSameAsLast, setIsOtpSameAsLast] = useState(false)
    const [otpError, setOtpError] = useState('')
    const [isLoading, setLoading] = useState<boolean | null>(null)
    const [mlSignupError, setMlSignupError] = useState<boolean | string>(false)

    const [onboardingStep, setOnboardingStep] = useState<
        SIGNUP_FORM_TYPE | undefined
    >(step)

    const { setUserData } = useContext(UserDataContext)
    const {
        multiAccount,
        setMultiAccount,
        allProvidersSignedUp,
        setAllProvidersSignedUp,
        selectedAccount,
        setSelectedAccount,
        signInDetails,
        unregisteredUserData,
        clearAuthFlow,
    } = useContext(AuthContext)
    const [onboardingDetails, setOnboardingDetails] =
        useState<IOnboardingDetails | null>(null)

    const handleSendOtp = useCallback(
        async (
            username: string,
            countrySelected: string,
            isResendRequest?: boolean
        ) => {
            const language = getLocalState<string>('i18nextLng', true)
            const signInResponse = isResendRequest
                ? await middleLayerRequestOtp({
                      username: username.trim().toLowerCase(),
                      countrySelected,
                      language: OTP_LANGUAGE(language as string),
                      isResendRequest: isResendRequest,
                  })
                : await middleLayerRequestOtp({
                      username: username.trim().toLowerCase(),
                      countrySelected,
                      language: OTP_LANGUAGE(language as string),
                  })
            if (signInResponse?.data.session) {
                setOtpSession(signInResponse?.data.session)
            }
        },
        []
    )

    //middleLayer
    const handleSignup = useCallback(
        async (
            values: OnboardingStepsFormType,
            { setFieldError }: FormikHelpers<OnboardingStepsFormType>
        ) => {
            //ML SIGN UP FLOW
            if (
                SIGNUP_FORM[stepIndex + 1] === SIGNUP_FORM_TYPE.OTPVERIFY &&
                ((Object.keys(multiAccount).length &&
                    allProvidersSignedUp !== undefined) ||
                    (Object.keys(multiAccount).length === 0 &&
                        unregisteredUserData !== null))
            ) {
                handleMlSignUp()
                return
            }

            if (SIGNUP_FORM[stepIndex] === SIGNUP_FORM_TYPE.USER) {
                let emailValue: string | undefined =
                    values.emailAddress.toLowerCase()
                setSubmittedOtp('')
                await handleSendOtp(
                    emailValue,
                    values.countrySelected.toLowerCase()
                )
            }

            if (SIGNUP_FORM[stepIndex] === SIGNUP_FORM_TYPE.OTPVERIFY) {
                const data = {
                    session: otpSession,
                    acceptTerms: values.acceptTerms,
                    appUserKey: values.appUserKey,
                    businessName: values.businessName ?? '',
                    country: values.countrySelected.toUpperCase(),
                    emailAddress: values.emailAddress.trim().toLowerCase(),
                    firstName: values.firstName,
                    lastName: values.lastName,
                    mobilePhoneNumber: values.mobilePhoneNumber
                        .trim()
                        .replace(/[-.\s()]/g, ''),
                    code: values.otp ?? '',
                    optInMarketingByEmail: values.emailConfirmation,
                    optInMarketingByPhone: values.emailConfirmation,
                    userRole: values.userRole,
                    beautyHealthCustomerId: values.beautyHealthCustomerId,
                    optInDataSharingAgreement: values.optInDataSharingAgreement,
                }
                setOnboardingDetails(data)
                const response = await handleMiddleLayerAuthentication(
                    data,
                    USER_AUTHENTICATION_TYPE.SIGNUP
                )
                if (!response.success) {
                    setSubmittedOtp(values?.otp ?? '')
                    if (response?.data?.session) {
                        setOtpSession(response?.data?.session)
                    }

                    if (
                        response.messageCode ===
                        MIDDLELAYER_RESPONSE_CODE.USER_ALREADY_EXIST
                    ) {
                        const {
                            data: {
                                token: jwtToken,
                                cumulusKeys: {
                                    idToken,
                                    accessToken,
                                    refreshToken,
                                },
                                refreshToken: middleLayerRefreshToken,
                            },
                        } = response
                        const tokens = {
                            jwtToken,
                            idToken,
                            accessToken,
                            refreshToken,
                            middleLayerRefreshToken,
                        }
                        Axios.initmiddleLayerInstance(tokens)
                        setUserData(response.data)
                        MixPanel.actions.people.set({
                            $first_name: response.data.firstName,
                            $last_name: response.data.lastName,
                            $email: response.data.email,
                            Role: response.data.userRole,
                        })
                        setUserExists(true)

                        MixPanel.actions.identify(response.data.appUserKey)

                        return
                    } else if (
                        response.messageCode ===
                        MIDDLELAYER_RESPONSE_CODE.INVALID_SESSION
                    ) {
                        setFieldError(
                            'otp',
                            t(MESSAGES.error.verificationCodeExpired) ?? ''
                        )
                        setOtpError(
                            t(MESSAGES.error.verificationCodeExpired) ?? ''
                        )
                        return
                    } else if (
                        response.messageCode ===
                        MIDDLELAYER_RESPONSE_CODE.USER_WITH_MOBILE_NUMBER_EXIST
                    ) {
                        setFieldError(
                            'otp',
                            t(MESSAGES.error.mobileNumberAlreadyInUse) ?? ''
                        )
                        setOtpError(
                            t(MESSAGES.error.mobileNumberAlreadyInUse) ?? ''
                        )
                        return
                    } else if (
                        response.messageCode ===
                        MIDDLELAYER_RESPONSE_CODE.USER_WITH_EMAIL_EXIST
                    ) {
                        setFieldError(
                            'otp',
                            t(MESSAGES.error.emailAlreadyInUse) ?? ''
                        )
                        setOtpError(t(MESSAGES.error.emailAlreadyInUse) ?? '')
                        return
                    } else if (
                        response.messageCode ===
                        MIDDLELAYER_RESPONSE_CODE.SOMETHING_WENT_WRONG
                    ) {
                        setFieldError(
                            'otp',
                            t('common_elements.something_wrong_text') ?? ''
                        )
                        setOtpError(
                            t('common_elements.something_wrong_text') ?? ''
                        )
                        return
                    } else if (response.message) {
                        setFieldError('otp', response.message ?? '')
                        setOtpError(t(response.message) ?? '')
                        return
                    }
                    setFieldError(
                        'otp',
                        t(MESSAGES.error.invalidVerificationCode) ?? ''
                    )
                    setOtpError(t(MESSAGES.error.invalidVerificationCode) ?? '')
                    return
                }

                if (
                    response.success &&
                    response.messageCode ===
                        MIDDLELAYER_RESPONSE_CODE.SUB_ACCOUNTS_FOUND
                ) {
                    const data = {
                        accessToken: response.data.accessToken,
                        idToken: response.data.idToken,
                        refreshToken: response.data.refreshToken,
                        subAccounts: response.data.subAccounts,
                    }
                    setMultiAccount(data)
                    setAllProvidersSignedUp(response.data.allProvidersSignedUp)
                    navigate(`${ROUTES.SELECTACCOUNT}`)
                    return
                }
                const {
                    data: {
                        token: jwtToken,
                        cumulusKeys: { idToken, accessToken, refreshToken },
                        refreshToken: middleLayerRefreshToken,
                    },
                } = response
                const tokens = {
                    jwtToken,
                    idToken,
                    accessToken,
                    refreshToken,
                    middleLayerRefreshToken,
                }
                Axios.initmiddleLayerInstance(tokens)
                setUserData(response.data)
                MixPanel.actions.people.set({
                    $first_name: response.data.firstName,
                    $last_name: response.data.lastName,
                    $email: response.data.email,
                    Role: response.data.userRole,
                })

                MixPanel.actions.identify(response.data.appUserKey)
            }
            if (SIGNUP_FORM.length - 1 === stepIndex) {
                navigate(ROUTES.DASHBOARD)
            } else if (SIGNUP_FORM.length - 1 > stepIndex) {
                setStepIndex((prev) => prev + 1)
                setProgress((prevProgress) => prevProgress + 100 / totalForms)
                navigate(`${ROUTES.SIGNUP}/${SIGNUP_FORM[stepIndex + 1]}/`)
            }
        },
        [
            stepIndex,
            handleSendOtp,
            otpSession,
            t,
            navigate,
            totalForms,
            setUserData,
            unregisteredUserData,
            allProvidersSignedUp,
            onboardingDetails,
            allProvidersSignedUp,
            multiAccount,
            unregisteredUserData,
        ]
    )

    const getFormStep = useCallback(
        (key: string) => {
            switch (key) {
                case SIGNUP_FORM_TYPE.USER:
                    return {
                        title: t('onboarding_name_page.onboarding_name_header'),
                        schema: onboardingStep1Schema(),
                        component: <Step1 />,
                    }
                case SIGNUP_FORM_TYPE.OTPVERIFY:
                    return {
                        title: t(
                            'code_verification_page.code_verification_header'
                        ),
                        schema: otpSchema,
                        component: <Step2 />,
                    }
            }
        },
        [t]
    )

    const handleBack = useCallback(() => {
        setLoading(false)
        setMlSignupError(false)
        if (!onboardingStep) {
            return
        }
        switch (onboardingStep) {
            case SIGNUP_FORM_TYPE.OTPVERIFY: {
                navigate(`${ROUTES.SIGNUP}/${SIGNUP_FORM_TYPE.USER}`)
                return
            }
            default:
                return
        }
    }, [onboardingStep, navigate])

    const handleDashboardRedirect = useCallback(() => {
        setUserExists(false)
        navigate(ROUTES.DASHBOARD)
    }, [navigate])

    const handleExistingUserBack = useCallback(() => {
        Axios.destroyMiddleLayerInstance()
        setUserExists(false)
        setUserData({})
        navigate(`${ROUTES.SIGNUP}/${SIGNUP_FORM_TYPE.USER}`)
    }, [navigate, setUserData])

    useEffect(() => {
        if (onboardingStep) {
            setStepIndex(
                SIGNUP_FORM.indexOf(onboardingStep) !== -1
                    ? SIGNUP_FORM.indexOf(onboardingStep)
                    : 0
            )
            setPage(getFormStep(onboardingStep))
            if (
                (!onboardingStep || onboardingStep === 'user-details') &&
                (Object.keys(multiAccount).length || unregisteredUserData)
            ) {
                return setProgress(100)
            }
            setProgress(
                ((SIGNUP_FORM.indexOf(onboardingStep) + 1) * 100) / totalForms
            )
        } else {
            setStepIndex(0)
            setProgress(100 / totalForms)
            setPage(getFormStep(SIGNUP_FORM_TYPE.USER))
        }
    }, [getFormStep, onboardingStep, totalForms, location])

    const handleMlSignUp = useCallback(
        async (data?: IMultiLocationSignUp) => {
            try {
                setLoading(true)
                setMlSignupError(false)

                const buildPayload = (
                    source: any,
                    isMultiAccount: boolean
                ): IMultiLocationSignUp => {
                    const isNewUser = unregisteredUserData?.isNewUser
                    const payload = {
                        ...source,
                        businessName: selectedAccount?.businessName || '',
                        firstName: source.firstName,
                        lastName: source.lastName,
                        emailAddress: source.emailAddress,
                        mobilePhoneNumber: source.mobilePhoneNumber
                            .trim()
                            .replace(/[-.\s()]/g, ''),
                        optInMarketingByEmail: source.optInMarketingByEmail,
                        optInMarketingByPhone: source.optInMarketingByPhone,
                        country: source.country,
                        userRole: isNewUser
                            ? UserRole.Esthetician
                            : UserRole.Provider,
                        beautyHealthCustomerId:
                            selectedAccount?.beautyHealthCustomerId || '',
                        idToken: isMultiAccount
                            ? multiAccount.idToken
                            : unregisteredUserData?.idToken,
                        accessToken: isMultiAccount
                            ? multiAccount.accessToken
                            : unregisteredUserData?.accessToken,
                        refreshToken: isMultiAccount
                            ? multiAccount.refreshToken
                            : unregisteredUserData?.refreshToken,
                        appUserKey: selectedAccount?.appUserKey || '',
                        mlpSingleSignUp: isMultiAccount
                            ? allProvidersSignedUp === false
                                ? false
                                : !selectedAccount?.locationSignedUp
                            : true,
                    }

                    Object.keys(payload).forEach((key) => {
                        if (payload[key] === '') {
                            delete payload[key]
                        }
                    })

                    return payload
                }

                let payload: IMultiLocationSignUp | undefined = undefined

                if (!data) {
                    if (
                        Object.keys(multiAccount).length &&
                        allProvidersSignedUp !== undefined
                    ) {
                        if (onboardingDetails) {
                            onboardingDetails['appUserKey'] =
                                selectedAccount?.appUserKey
                            if (signInDetails?.isEmail) {
                                onboardingDetails.emailAddress =
                                    signInDetails.username
                                onboardingDetails.userRole = UserRole.Provider
                                onboardingDetails.businessName =
                                    selectedAccount?.businessName
                                onboardingDetails.idToken = multiAccount.idToken
                                onboardingDetails.accessToken =
                                    multiAccount.accessToken
                                onboardingDetails.refreshToken =
                                    multiAccount.refreshToken
                            }
                            payload = buildPayload(onboardingDetails, true)
                        }
                    } else if (
                        Object.keys(multiAccount).length === 0 &&
                        unregisteredUserData !== null
                    ) {
                        if (onboardingDetails) {
                            if (signInDetails?.isEmail) {
                                onboardingDetails.emailAddress =
                                    signInDetails.username
                                onboardingDetails.userRole =
                                    unregisteredUserData.isNewUser
                                        ? UserRole.Esthetician
                                        : UserRole.Provider
                            } else {
                                onboardingDetails.mobilePhoneNumber =
                                    signInDetails?.username || ''
                                onboardingDetails.mobilePhoneCountry =
                                    signInDetails?.countrySelected
                                onboardingDetails.userRole =
                                    unregisteredUserData.isNewUser
                                        ? UserRole.Esthetician
                                        : UserRole.Provider
                            }
                            payload = buildPayload(onboardingDetails, false)
                            if (unregisteredUserData.isNewUser) {
                                payload.initiateEstiSignUp = true
                            }
                        }
                    }
                }

                if (payload || data) {
                    const response = await handleMiddleLayerAuthentication(
                        (data ? data : payload) as IMultiLocationSignUp,
                        USER_AUTHENTICATION_TYPE.MULTI_ACCOUNT_SIGN_UP
                    )

                    setLoading(false)

                    if (response.success) {
                        const {
                            data: {
                                token: jwtToken,
                                cumulusKeys: {
                                    idToken,
                                    accessToken,
                                    refreshToken,
                                },
                                refreshToken: middleLayerRefreshToken,
                            },
                        } = response
                        const tokens = {
                            jwtToken,
                            idToken,
                            accessToken,
                            refreshToken,
                            middleLayerRefreshToken,
                        }

                        Axios.initmiddleLayerInstance(tokens)
                        setUserData(response.data)
                        MixPanel.actions.people.set({
                            $first_name: response.data.firstName,
                            $last_name: response.data.lastName,
                            $email: response.data.email,
                            Role: response.data.userRole,
                        })
                        MixPanel.actions.identify(response.data.appUserKey)
                        navigate(ROUTES.DASHBOARD)
                    } else {
                        setMlSignupError(response.message)
                        navigate(`${ROUTES.SIGNUP}/${SIGNUP_FORM_TYPE.USER}`)
                    }
                } else {
                    throw new Error()
                }
            } catch (e) {
                setMlSignupError(true)
                setLoading(false)
                navigate(`${ROUTES.SIGNUP}/${SIGNUP_FORM_TYPE.USER}`)
            }
        },
        [
            mlSignupError,
            onboardingDetails,
            allProvidersSignedUp,
            multiAccount,
            signInDetails,
            unregisteredUserData,
            selectedAccount,
            setOnboardingDetails,
        ]
    )

    const clearAuthenticationFlow = useCallback(() => {
        clearAuthFlow()
        navigate(`${ROUTES.SIGNIN}`)
    }, [clearAuthFlow])

    const mappedValue = useMemo(
        () => ({
            page,
            progress,
            stepIndex,
            handleSignup,
            handleBack,
            handleResendOtp: handleSendOtp,
            submittedOtp,
            setIsOtpSameAsLast,
            isOtpSameAsLast,
            otpError,
            multiAccount,
            setMultiAccount,
            setStep: setOnboardingStep,
            allProvidersSignedUp,
            onboardingDetails,
            setOnboardingDetails,
            otpSession,
            selectedAccount,
            setSelectedAccount,
            unregisteredUserData,
            isLoading,
            setLoading,
            mlSignupError,
            clearAuthenticationFlow,
            setMlSignupError,
            signUp: handleMlSignUp,
            signInDetails,
        }),
        [
            page,
            progress,
            stepIndex,
            handleSignup,
            handleBack,
            handleSendOtp,
            isOtpSameAsLast,
            submittedOtp,
            otpError,
            multiAccount,
            setMultiAccount,
            allProvidersSignedUp,
            onboardingDetails,
            setOnboardingDetails,
            otpSession,
            setSelectedAccount,
            selectedAccount,
            unregisteredUserData,
            isLoading,
            setLoading,
            mlSignupError,
            location,
            setMlSignupError,
            handleMlSignUp,
            signInDetails,
        ]
    )

    return (
        <OnboardingFormContext.Provider value={mappedValue}>
            {children}
            {userExists && (
                <UserExistsModal
                    open={userExists}
                    handleSuccessClick={handleDashboardRedirect}
                    closeModal={handleExistingUserBack}
                />
            )}
        </OnboardingFormContext.Provider>
    )
}

export default OnboardingFormProvider
